// @flow

import * as React from 'react';

import type {
  Actions,
  SaveStatus,
  ActionStatus,
  UserViewState,
  ColumnsMap,
} from '../types';
import ColumnsAction from './actions/ColumnsAction';
import ColumnsActionLegacy from './actions/Legacy/ColumnsAction';
import CopyAction from './actions/CopyAction';
import ExportAction from './actions/ExportAction';
import ExportODataAction from './actions/ExportODataAction';
import RefreshAction from './actions/RefreshAction';
import ResetAction from './actions/ResetAction';
import SaveViewAction from './actions/SaveViewAction';
import SaveViewDropdown from './actions/SaveViewDropdown';
import FieldViewerAction from './actions/FieldViewerAction';
import MultiSortAction from './actions/MultiSortAction';

const fn = () => {};

type Props = {
  grid: any,
  collection: any,
  currentItem: any,
  defaultColumnsMapping: ColumnsMap,
  columnsMapping: ColumnsMap,
  selectedItems: number,
  children: Actions => React.Node,
  moduleName: string,
  saveStatus: SaveStatus,
  loading: boolean,
  onViewSaved: (SaveStatus, UserViewState, ?string) => typeof undefined,
  onOrderCreated?: ActionStatus => typeof undefined,
  onOrderActivated?: ActionStatus => typeof undefined,
  onOrderRemoved?: ActionStatus => typeof undefined,
  onItemAdded?: ActionStatus => typeof undefined,
  onViewReset: () => mixed,
  onApplyViewSettings?: (viewSettings: Object) => mixed,
  onColumnsMappingChange: ColumnsMap => mixed,
  onViewLoaded?: (viewName: ?string) => void,
  userViewState: UserViewState,
  currentViewName?: ?string,
  refetch: () => mixed,
  hasAllColumnsHidden: boolean,
  hasDataToDisplay: boolean,
  frozenColumns: number,
  state?: string,
  month?: string,
  orderCreateStatus?: ActionStatus,
  orderActiveStatus?: ActionStatus,
  itemAddedStatus?: ActionStatus,
  groups: Array<string>,
  onExported: () => mixed,
  onExporting: () => mixed,
  useLegacyColumnsManager?: boolean,
  toggleMultiSort: () => void,
  multiColumnSort: boolean,
  setExportProgress: (number | null) => void,
  exportWithStyles?: boolean,
  useMultipleViews?: boolean,
  getGridFilterDefinition?: () => ?string,
  skipStateMonthRefetch?: boolean,
};

type State = {};

export default class ActionsHeader extends React.Component<Props, State> {
  static defaultProps = {
    defaultColumnsMapping: [],
    columnsMapping: [{}],
    groups: [],
    useMultipleViews: false,
    skipStateMonthRefetch: false,
  };

  getGlobalDisabledState = () => this.props.hasAllColumnsHidden;

  componentDidUpdate(prevProps: Props) {
    /**
     * Automatically refresh the grid if the user changes the state (territorial)
     * or month - UNLESS skipStateMonthRefetch is true.
     * 
     * For Inventory module, we skip this refetch because price filtering is
     * handled client-side by PriceUpdateEffect and updatePriceFields(). The
     * server sends all prices in linkedPrices, and the client filters them
     * locally when state/month changes. This reduces switch time from ~67s
     * to ~100ms for large datasets.
     * 
     * Other modules that don't have client-side filtering will still refetch.
     */
    if (
      !this.props.skipStateMonthRefetch &&
      (prevProps.state !== this.props.state ||
       prevProps.month !== this.props.month)
    ) {
      this.props.refetch();
    }
  }

  // Handler for resetting to default view - used by SaveViewDropdown
  handleResetToDefault = () => {
    // Call the original reset handler to apply default settings to the grid
    this.props.onViewReset();
    
    // Clear the current view name to show "Default View"
    if (this.props.onViewLoaded) {
      this.props.onViewLoaded(null);
    }
  };

  // Handler for applying a saved view's settings
  handleApplyViewSettings = (viewSettings, viewName) => {
    // Apply the view settings to the grid
    if (this.props.onApplyViewSettings) {
      this.props.onApplyViewSettings(viewSettings);
    }
    
    // Update the current view name
    if (this.props.onViewLoaded) {
      this.props.onViewLoaded(viewName);
    }
  };

  render() {
    const isOneRowSelected = this.props.currentItem;
    const noRowsSelected =
      this.props.selectedItems === 0 || this.props.selectedItems === undefined;
    const onOrderCreated = this.props.onOrderCreated || fn;
    const onOrderActivated = this.props.onOrderActivated || fn;
    const onItemAdded = this.props.onItemAdded || fn;
    const onOrderRemoved = this.props.onOrderRemoved || fn;

    return this.props.children({
      ColumnsAction: ({ disabled }: any) => {
        return this.props.useLegacyColumnsManager === true
          ?
          <ColumnsActionLegacy
            disabled={
              !this.props.hasDataToDisplay || this.props.loading || disabled
            }
            collection={this.props.collection}
            onColumnsChange={this.props.onColumnsMappingChange}
            columnsMapping={this.props.columnsMapping}
            defaultColumnsMapping={this.props.defaultColumnsMapping}
          />
          :
          <ColumnsAction
            disabled={
              !this.props.hasDataToDisplay || this.props.loading || disabled
            }
            collection={this.props.collection}
            onColumnsChange={this.props.onColumnsMappingChange}
            columnsMapping={this.props.columnsMapping}
            defaultColumnsMapping={this.props.defaultColumnsMapping}
          />
      },
      CopyAction: ({ disabled }: any) => (
        <CopyAction
          grid={this.props.grid}
          disabled={
            this.getGlobalDisabledState() ||
            !this.props.hasDataToDisplay ||
            !isOneRowSelected ||
            this.props.loading ||
            disabled
          }
          collection={this.props.collection}
        />
      ),
      ExportAction: ({ disabled }: any) => (
        <ExportAction
          grid={this.props.grid}
          collection={this.props.collection}
          moduleName={this.props.moduleName}
          disabled={
            this.getGlobalDisabledState() ||
            !this.props.hasDataToDisplay ||
            this.props.loading ||
            disabled
          }
        />
      ),
      ExportODataAction: ({ disabled }: any) => (
        <ExportODataAction
          grid={this.props.grid}
          collection={this.props.collection}
          moduleName={this.props.moduleName}
          disabled={
            this.getGlobalDisabledState() ||
            !this.props.hasDataToDisplay ||
            this.props.loading ||
            disabled ||
            this.props.status === 'exporting'
          }
          onExporting={this.props.onExporting}
          onExported={this.props.onExported}
          setExportProgress={this.props.setExportProgress}
          exportWithStyles={this.props.exportWithStyles}
        />
      ),
      RefreshAction: ({ disabled }: any) => (
        <RefreshAction
          disabled={
            this.getGlobalDisabledState() || this.props.loading || disabled
          }
          refetch={this.props.refetch}
        />
      ),
      ResetAction: ({ disabled, filters }: any) => (
        <ResetAction
          disabled={this.props.loading || disabled}
          moduleName={this.props.moduleName}
          defaultColumnsMapping={this.props.defaultColumnsMapping}
          defaultGroups={this.props.defaultSettings.defaultGroupPanels}
          defaultSorting={this.props.defaultSettings.defaultSorting}
          userViewState={this.props.userViewState}
          collection={this.props.collection}
          filters={filters}
          onViewSaved={this.props.onViewSaved}
          onViewReset={this.props.onViewReset}
          handleResetAnimation={this.props.handleResetAnimation}
        />
      ),
      SaveViewAction: ({ disabled, filters }: any) => {
        // Use new SaveViewDropdown when useMultipleViews is enabled
        if (this.props.useMultipleViews && this.props.onViewLoaded) {
          return (
            <SaveViewDropdown
              moduleName={this.props.moduleName}
              columnsMapping={this.props.columnsMapping}
              disabled={
                this.getGlobalDisabledState() ||
                !this.props.hasDataToDisplay ||
                this.props.loading ||
                disabled
              }
              onViewSaved={this.props.onViewSaved}
              onViewLoaded={this.props.onViewLoaded}
              onResetToDefault={this.handleResetToDefault}
              onApplyViewSettings={this.handleApplyViewSettings}
              saveStatus={this.props.saveStatus}
              currentViewName={this.props.currentViewName}
              frozenColumns={this.props.frozenColumns}
              collection={this.props.collection}
              filters={filters}
              getGridFilterDefinition={this.props.getGridFilterDefinition}
              grid={this.props.grid}
            />
          );
        }

        // Fall back to legacy SaveViewAction
        return (
          <SaveViewAction
            moduleName={this.props.moduleName}
            columnsMapping={this.props.columnsMapping}
            disabled={
              this.getGlobalDisabledState() ||
              !this.props.hasDataToDisplay ||
              this.props.saveStatus === '' ||
              this.props.saveStatus === 'saved' ||
              this.props.loading ||
              disabled
            }
            onViewSaved={this.props.onViewSaved}
            saveStatus={this.props.saveStatus}
            userViewState={this.props.userViewState}
            frozenColumns={this.props.frozenColumns}
            collection={this.props.collection}
            filters={filters}
          />
        );
      },
      FieldViewerAction: (props: any) => (
        <FieldViewerAction
          disabled={
            this.getGlobalDisabledState() ||
            !this.props.hasDataToDisplay ||
            !isOneRowSelected ||
            this.props.loading ||
            props.disabled
          }
          item={this.props.currentItem}
          title={props.title}
          field={props.field}
        />
      ),
      MultiSortAction: (props: any) => (
        <MultiSortAction
          toggleMultiSort={this.props.toggleMultiSort}
          multiColumnSort={this.props.multiColumnSort}
        />
      ),
      hasAllColumnsHidden: this.props.hasAllColumnsHidden,
      isOneRowSelected,
      noRowsSelected,
      hasDataToDisplay: this.props.hasDataToDisplay,
      loading: this.props.loading,
      currentItem: this.props.currentItem,
      getGlobalDisabledState: this.getGlobalDisabledState,
      onOrderCreated,
      onOrderActivated,
      onOrderRemoved,
      onItemAdded,
      grid: this.props.grid,
      groups: this.props.groups,
      orderCreateStatus: this.props.orderCreateStatus,
      orderActiveStatus: this.props.orderActiveStatus,
      itemAddedStatus: this.props.itemAddedStatus,
      moduleName: this.props.moduleName,
    });
  }
}
